// services/narration/templates.fr.ts
import type { EvidenceAtom } from "../../types";
import { bound } from "./templateEngine";

/**
 * CAPACITÉ MUSICALE — evidence-bound, determinist
 * Requires: STABILITY_PCT, SCORE_TOTAL
 */
export function capaciteMusicale(f: Record<string, EvidenceAtom>) {
  if (!f.STABILITY_PCT || !f.SCORE_TOTAL) return bound("", []);

  const stab = Number(f.STABILITY_PCT.value);
  const score = Number(f.SCORE_TOTAL.value);

  let label = "";
  if (stab >= 85) label = "Capacité musicale solide";
  else if (stab >= 70) label = "Capacité musicale en construction";
  else label = "Capacité musicale fragile";

  return bound(
    `${label} — stabilité rythmique ${stab}% • score global ${score}/100.`,
    [f.STABILITY_PCT, f.SCORE_TOTAL]
  );
}

/**
 * NIVEAU ATTEINT — determinist classification
 * Requires: SCORE_TOTAL, STABILITY_PCT
 */
export function niveauAtteint(f: Record<string, EvidenceAtom>) {
  if (!f.SCORE_TOTAL || !f.STABILITY_PCT) return bound("", []);

  const score = Number(f.SCORE_TOTAL.value);
  const stab = Number(f.STABILITY_PCT.value);

  let niveau = "";
  if (score >= 85 && stab >= 85) niveau = "Niveau confirmé";
  else if (score >= 70 && stab >= 70) niveau = "Niveau intermédiaire";
  else niveau = "Niveau débutant en consolidation";

  return bound(
    `${niveau} — score ${score}/100 • stabilité ${stab}%.`,
    [f.SCORE_TOTAL, f.STABILITY_PCT]
  );
}

/**
 * POTENTIEL — observation-based reading (not prophecy)
 * Requires: QUALITY_SCORE, CONFIDENCE_PCT
 */
export function potentiel(f: Record<string, EvidenceAtom>) {
  if (!f.QUALITY_SCORE || !f.CONFIDENCE_PCT) return bound("", []);

  const quality = Number(f.QUALITY_SCORE.value);
  const confidence = Number(f.CONFIDENCE_PCT.value);

  let lecture = "";
  if (quality >= 80 && confidence >= 80)
    lecture = "Potentiel de progression élevé à court terme";
  else if (quality >= 65)
    lecture = "Potentiel de progression présent avec régularité";
  else
    lecture = "Potentiel limité à court terme, bases à stabiliser";

  return bound(
    `${lecture} — qualité ${quality}/100 • confiance ${confidence}%.`,
    [f.QUALITY_SCORE, f.CONFIDENCE_PCT]
  );
}

/**
 * DIAGNOSTIC PÉDAGOGIQUE — prioritization based on measured bottleneck
 * Requires: TIMING_AVG_MS, STABILITY_PCT
 */
export function diagnosticPedagogique(f: Record<string, EvidenceAtom>) {
  if (!f.TIMING_AVG_MS || !f.STABILITY_PCT) return bound("", []);

  const timing = Number(f.TIMING_AVG_MS.value);
  const stab = Number(f.STABILITY_PCT.value);

  let diag = "";
  if (timing > 45) diag = "Priorité : synchronisation rythmique";
  else if (stab < 75) diag = "Priorité : stabilité du tempo";
  else diag = "Aucune faiblesse majeure détectée";

  return bound(
    `${diag} — écart moyen ${timing} ms • stabilité ${stab}%.`,
    [f.TIMING_AVG_MS, f.STABILITY_PCT]
  );
}
